/**
 * Flashcard Service - Quản lý storage và CRUD operations cho flashcards
 * Hybrid storage: Backend API (nếu có) hoặc localStorage (fallback)
 */

import type { Flashcard, FlashcardDraft, PDFDocument, FlashcardCollection } from '../../types/flashcard'
import { flashcardBackendService } from './FlashcardBackendService'
import { staticFlashcardService } from './StaticFlashcardService'
import { autoExportService } from './AutoExportService'

const STORAGE_KEYS = {
  FLASHCARDS: 'vat_li_365_flashcards',
  DOCUMENTS: 'vat_li_365_pdf_documents',
  COLLECTIONS: 'vat_li_365_flashcard_collections'
}

const USE_BACKEND = false // ✅ Set true để dùng backend API
const USE_STATIC_FILES = true // ✅ Set true để dùng static files (WordPress hosting)

class FlashcardService {
  /**
   * Check if should use backend
   */
  private shouldUseBackend(): boolean {
    return USE_BACKEND && flashcardBackendService.isAuthenticated()
  }

  /**
   * Get user email from auth
   * Priority: auth_user (AuthContext) > google_auth_user (legacy)
   */
  private getUserEmail(): string {
    // Try new auth system first
    const authUserString = localStorage.getItem('auth_user');
    if (authUserString) {
      try {
        const authUser = JSON.parse(authUserString);
        if (authUser.email) {
          console.log('✅ [FlashcardService] Using email from auth_user:', authUser.email);
          return authUser.email;
        }
      } catch (error) {
        console.error('Error parsing auth_user:', error);
      }
    }
    
    // Fallback to legacy
    const userDataString = localStorage.getItem('google_auth_user')
    if (!userDataString) {
      console.warn('⚠️ [FlashcardService] No user found, using default_user');
      return 'default_user';
    }
    
    try {
      const userData = JSON.parse(userDataString)
      console.log('✅ [FlashcardService] Using email from google_auth_user:', userData.email);
      return userData.email || 'default_user'
    } catch {
      return 'default_user'
    }
  }
  /**
   * Save flashcards từ drafts
   */
  async saveFlashcards(
    drafts: FlashcardDraft[],
    documentId: string,
    documentName: string,
    userId: string = 'default_user'
  ): Promise<Flashcard[]> {
    try {
      // ✅ Use backend nếu user đã login
      if (this.shouldUseBackend()) {
        console.log('💾 Saving flashcards to BACKEND...')
        return await flashcardBackendService.saveFlashcards(drafts, documentId, documentName)
      }
      
      // Fallback: localStorage
      console.log('💾 Saving flashcards to LOCALSTORAGE...')
      const existingFlashcards = await this.getAllFlashcards()
      
      const newFlashcards: Flashcard[] = drafts.map(draft => ({
        id: `flashcard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        authorId: userId,
        userId, // For backward compatibility
        title: draft.front.substring(0, 50), // Use first 50 chars of front as title
        documentId,
        documentName,
        front: draft.front,
        back: draft.back,
        tags: draft.tags || [],
        difficulty: draft.difficulty,
        isPublic: false,
        views: 0,
        likes: 0,
        reviewCount: 0,
        confidence: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      }))

      const allFlashcards = [...existingFlashcards, ...newFlashcards]
      
      localStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(allFlashcards))
      
      console.log('✅ Saved', newFlashcards.length, 'flashcards')
      
      // 🚀 AUTO-EXPORT: Tự động export ra file JSON trong DEV mode
      try {
        const documents = await this.getAllPDFDocuments()
        await autoExportService.autoExport(allFlashcards, documents)
      } catch (exportError) {
        console.warn('⚠️ Auto-export warning (không ảnh hưởng save):', exportError)
      }
      
      return newFlashcards
    } catch (error) {
      console.error('Error saving flashcards:', error)
      throw new Error('Không thể lưu flashcards')
    }
  }

  /**
   * Get all flashcards
   */
  async getAllFlashcards(): Promise<Flashcard[]> {
    try {
      // Priority 1: Static files (for WordPress hosting)
      if (USE_STATIC_FILES) {
        const userEmail = this.getUserEmail()
        const hasStatic = await staticFlashcardService.hasStaticData(userEmail)
        
        if (hasStatic) {
          console.log('📥 Loading flashcards from STATIC FILES...')
          const data = await staticFlashcardService.loadUserData(userEmail)
          if (data) return data.flashcards
        }
      }
      
      // Priority 2: Backend API
      if (this.shouldUseBackend()) {
        console.log('📥 Loading flashcards from BACKEND...')
        return await flashcardBackendService.getAllFlashcards()
      }
      
      // Priority 3: localStorage (fallback)
      console.log('📥 Loading flashcards from LOCALSTORAGE...')
      const data = localStorage.getItem(STORAGE_KEYS.FLASHCARDS)
      if (!data) return []
      
      const flashcards = JSON.parse(data)
      
      // Convert date strings to Date objects
      return flashcards.map((f: any) => ({
        ...f,
        createdAt: new Date(f.createdAt),
        updatedAt: new Date(f.updatedAt),
        lastReviewed: f.lastReviewed ? new Date(f.lastReviewed) : undefined,
        nextReview: f.nextReview ? new Date(f.nextReview) : undefined
      }))
    } catch (error) {
      console.error('Error loading flashcards:', error)
      return []
    }
  }

  /**
   * Get flashcards by document ID
   */
  async getFlashcardsByDocument(documentId: string): Promise<Flashcard[]> {
    const allFlashcards = await this.getAllFlashcards()
    return allFlashcards.filter(f => f.documentId === documentId)
  }

  /**
   * Get flashcards by user ID
   */
  async getFlashcardsByUser(userId: string): Promise<Flashcard[]> {
    const allFlashcards = await this.getAllFlashcards()
    return allFlashcards.filter(f => f.userId === userId)
  }

  /**
   * Update a flashcard
   */
  async updateFlashcard(id: string, updates: Partial<Flashcard>): Promise<Flashcard | null> {
    try {
      const allFlashcards = await this.getAllFlashcards()
      const index = allFlashcards.findIndex(f => f.id === id)
      
      if (index === -1) {
        throw new Error('Flashcard không tồn tại')
      }

      allFlashcards[index] = {
        ...allFlashcards[index],
        ...updates,
        updatedAt: new Date()
      }

      localStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(allFlashcards))
      
      return allFlashcards[index]
    } catch (error) {
      console.error('Error updating flashcard:', error)
      return null
    }
  }

  /**
   * Delete a flashcard
   */
  async deleteFlashcard(id: string): Promise<boolean> {
    try {
      const allFlashcards = await this.getAllFlashcards()
      const filtered = allFlashcards.filter(f => f.id !== id)
      
      localStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(filtered))
      
      return true
    } catch (error) {
      console.error('Error deleting flashcard:', error)
      return false
    }
  }

  /**
   * Delete all flashcards for a document
   */
  async deleteFlashcardsByDocument(documentId: string): Promise<boolean> {
    try {
      const allFlashcards = await this.getAllFlashcards()
      const filtered = allFlashcards.filter(f => f.documentId !== documentId)
      
      localStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(filtered))
      
      return true
    } catch (error) {
      console.error('Error deleting flashcards:', error)
      return false
    }
  }

  /**
   * Save PDF document metadata
   */
  async savePDFDocument(document: Omit<PDFDocument, 'id'>): Promise<PDFDocument> {
    try {
      // ✅ Use backend nếu user đã login
      if (this.shouldUseBackend()) {
        console.log('💾 Saving document to BACKEND...')
        return await flashcardBackendService.savePDFDocument(document)
      }
      
      // Fallback: localStorage
      console.log('💾 Saving document to LOCALSTORAGE...')
      const existingDocuments = await this.getAllPDFDocuments()
      
      const newDocument: PDFDocument = {
        ...document,
        id: `pdf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }

      const allDocuments = [...existingDocuments, newDocument]
      
      localStorage.setItem(STORAGE_KEYS.DOCUMENTS, JSON.stringify(allDocuments))
      
      console.log('✅ Saved PDF document:', newDocument.filename)
      
      return newDocument
    } catch (error) {
      console.error('Error saving PDF document:', error)
      throw new Error('Không thể lưu thông tin tài liệu')
    }
  }

  /**
   * Get all PDF documents
   */
  async getAllPDFDocuments(): Promise<PDFDocument[]> {
    try {
      // Priority 1: Static files (for WordPress hosting)
      if (USE_STATIC_FILES) {
        const userEmail = this.getUserEmail()
        const hasStatic = await staticFlashcardService.hasStaticData(userEmail)
        
        if (hasStatic) {
          console.log('📥 Loading documents from STATIC FILES...')
          const data = await staticFlashcardService.loadUserData(userEmail)
          if (data) return data.documents
        }
      }
      
      // Priority 2: Backend API
      if (this.shouldUseBackend()) {
        console.log('📥 Loading documents from BACKEND...')
        return await flashcardBackendService.getAllPDFDocuments()
      }
      
      // Priority 3: localStorage (fallback)
      console.log('📥 Loading documents from LOCALSTORAGE...')
      const data = localStorage.getItem(STORAGE_KEYS.DOCUMENTS)
      if (!data) return []
      
      const documents = JSON.parse(data)
      
      return documents.map((d: any) => ({
        ...d,
        uploadedAt: new Date(d.uploadedAt)
      }))
    } catch (error) {
      console.error('Error loading PDF documents:', error)
      return []
    }
  }

  /**
   * Get PDF document by ID
   */
  async getPDFDocument(documentId: string): Promise<PDFDocument | null> {
    const allDocuments = await this.getAllPDFDocuments()
    return allDocuments.find(d => d.id === documentId) || null
  }

  /**
   * Delete PDF document and its flashcards
   */
  async deletePDFDocument(documentId: string): Promise<boolean> {
    try {
      // ✅ Use backend nếu user đã login
      if (this.shouldUseBackend()) {
        console.log('🗑️ Deleting document from BACKEND...')
        return await flashcardBackendService.deletePDFDocument(documentId)
      }
      
      // Fallback: localStorage
      console.log('🗑️ Deleting document from LOCALSTORAGE...')
      // Delete flashcards
      await this.deleteFlashcardsByDocument(documentId)
      
      // Delete document
      const allDocuments = await this.getAllPDFDocuments()
      const filtered = allDocuments.filter(d => d.id !== documentId)
      
      localStorage.setItem(STORAGE_KEYS.DOCUMENTS, JSON.stringify(filtered))
      
      return true
    } catch (error) {
      console.error('Error deleting PDF document:', error)
      return false
    }
  }

  /**
   * Update flashcard after review
   */
  async recordReview(
    flashcardId: string,
    confidence: number, // 0-5
    correct: boolean
  ): Promise<void> {
    // ✅ Use backend nếu user đã login
    if (this.shouldUseBackend()) {
      console.log('📝 Recording review to BACKEND...')
      return await flashcardBackendService.recordReview(flashcardId, confidence, correct)
    }
    
    // Fallback: localStorage
    console.log('📝 Recording review to LOCALSTORAGE...')
    const allFlashcards = await this.getAllFlashcards()
    const flashcard = allFlashcards.find(f => f.id === flashcardId)
    if (!flashcard) return

    const newConfidence = correct 
      ? Math.min(5, flashcard.confidence + 1)
      : Math.max(0, flashcard.confidence - 1)

    // Calculate next review date (simple spaced repetition)
    const intervals = [1, 3, 7, 14, 30, 60] // days
    const intervalIndex = Math.min(newConfidence, intervals.length - 1)
    const nextReviewDate = new Date()
    nextReviewDate.setDate(nextReviewDate.getDate() + intervals[intervalIndex])

    await this.updateFlashcard(flashcardId, {
      reviewCount: flashcard.reviewCount + 1,
      confidence: newConfidence,
      lastReviewed: new Date(),
      nextReview: nextReviewDate
    })
  }

  /**
   * Get flashcards due for review
   */
  async getFlashcardsDueForReview(userId: string): Promise<Flashcard[]> {
    const userFlashcards = await this.getFlashcardsByUser(userId)
    const now = new Date()
    
    return userFlashcards.filter(f => {
      // Never reviewed OR review date has passed
      return !f.nextReview || f.nextReview <= now
    })
  }

  /**
   * Get statistics
   */
  async getStatistics(userId: string): Promise<{
    total: number
    byDifficulty: { easy: number; medium: number; hard: number }
    byConfidence: number[]
    dueForReview: number
    totalDocuments: number
  }> {
    const userFlashcards = await this.getFlashcardsByUser(userId)
    const userDocuments = await this.getAllPDFDocuments()
    const filteredDocs = userDocuments.filter(d => d.userId === userId)
    const dueForReview = await this.getFlashcardsDueForReview(userId)

    return {
      total: userFlashcards.length,
      byDifficulty: {
        easy: userFlashcards.filter(f => f.difficulty === 'easy').length,
        medium: userFlashcards.filter(f => f.difficulty === 'medium').length,
        hard: userFlashcards.filter(f => f.difficulty === 'hard').length
      },
      byConfidence: [0, 1, 2, 3, 4, 5].map(conf =>
        userFlashcards.filter(f => f.confidence === conf).length
      ),
      dueForReview: dueForReview.length,
      totalDocuments: filteredDocs.length
    }
  }

  /**
   * Export flashcards to JSON
   */
  exportFlashcards(userId: string): string {
    const userFlashcards = this.getFlashcardsByUser(userId)
    return JSON.stringify(userFlashcards, null, 2)
  }

  /**
   * Clear all data (for testing)
   */
  clearAllData(): void {
    localStorage.removeItem(STORAGE_KEYS.FLASHCARDS)
    localStorage.removeItem(STORAGE_KEYS.DOCUMENTS)
    localStorage.removeItem(STORAGE_KEYS.COLLECTIONS)
    console.log('🗑️ Cleared all flashcard data')
  }
}

// Export singleton instance
export const flashcardService = new FlashcardService()
export default flashcardService

